import {Fragment, useEffect, useRef, useState} from "react";
import {
    Alert,
    Box,
    Button,
    CircularProgress,
    Container,
    Paper,
    Stack,
    Typography
} from "@mui/material";
import {Link} from "react-router-dom";
import {login, logout} from "../shared/keycloak";
import useSessao from "../shared/useSessao";
import {api} from "../api/DpgeIntegracaoEsfingeApi";
import IdentificacaoSessao from "../components/header/IdentificacaoSessao";
import AppearanceDialog from "../components/header/AppearanceDialog";

export default function AcessoAplicacao({children}) {
    const session = useSessao();
    return session.hasAccess ? (
        <Fragment key={session.revision}>{children}</Fragment>
    ) : (
        <TelaAcesso session={session} />
    );
}

export function TelaAcesso({session, moduleDenied = false}) {
    const [appearanceOpen, setAppearanceOpen] = useState(false);
    const mounted = useRef(true);
    useEffect(
        () => () => {
            mounted.current = false;
        },
        []
    );
    const [busy, setBusy] = useState(false);
    const [message, setMessage] = useState("");
    const status = moduleDenied ? "denied" : session.status;
    const titles = {
        initializing: "Verificando sua sessão",
        configuration: "Autenticação não configurada",
        error: "Não foi possível iniciar o acesso",
        expired: "Sua sessão expirou",
        "signed-out": "Sessão encerrada",
        denied: "Acesso não autorizado"
    };
    const descriptions = {
        initializing:
            "Aguarde a verificação do seu acesso ao conector e-Sfinge.",
        configuration:
            "A configuração de autenticação deste ambiente está ausente ou incompleta. Solicite a correção ao suporte técnico.",
        error: "O serviço de autenticação não pôde ser iniciado. Tente novamente ou entre em contato com o suporte.",
        expired:
            "Entre novamente para continuar. Operações de escrita não são repetidas automaticamente.",
        "signed-out":
            "Você saiu da conta. Entre novamente quando quiser continuar.",
        denied: moduleDenied
            ? "Sua conta não tem permissão para este módulo. Você pode continuar usando as funcionalidades às quais tem acesso."
            : "Sua conta está autenticada, mas não tem permissão para acessar as operações do conector e-Sfinge neste ambiente."
    };
    async function execute(action) {
        setBusy(true);
        setMessage("");
        try {
            await action();
            if (mounted.current)
                setMessage(
                    "Acesso verificado. Se a restrição permanecer, solicite a liberação ao responsável pelo sistema."
                );
        } catch (error) {
            if (mounted.current)
                setMessage(
                    error?.message ||
                        "Não foi possível verificar o acesso. Tente novamente."
                );
        } finally {
            if (mounted.current) setBusy(false);
        }
    }
    return (
        <Box sx={{minHeight: "100vh", bgcolor: "background.default"}}>
            <Box
                component="header"
                sx={{
                    bgcolor: "header.main",
                    color: "header.contrastText",
                    py: 2
                }}
            >
                <Container maxWidth="lg">
                    <Stack
                        direction={{xs: "column", sm: "row"}}
                        justifyContent="space-between"
                        spacing={2}
                    >
                        <Stack
                            direction="row"
                            alignItems="center"
                            spacing={1.5}
                        >
                            <Box
                                component="img"
                                src={`${process.env.PUBLIC_URL}/favicon-esfinge.png`}
                                alt="Símbolo do e-Sfinge"
                                sx={{
                                    width: 38,
                                    height: 38,
                                    bgcolor: "white",
                                    p: 0.5,
                                    borderRadius: 1
                                }}
                            />
                            <Typography variant="h6">
                                Conector e-Sfinge
                            </Typography>
                        </Stack>
                        <Stack direction="row" spacing={2} alignItems="center">
                            <IdentificacaoSessao />
                            <Button
                                color="inherit"
                                onClick={() => setAppearanceOpen(true)}
                            >
                                Aparência
                            </Button>
                        </Stack>
                    </Stack>
                </Container>
            </Box>
            <AppearanceDialog
                open={appearanceOpen}
                onClose={() => setAppearanceOpen(false)}
            />
            <Container component="main" maxWidth="sm" sx={{py: {xs: 4, sm: 9}}}>
                <Paper
                    variant="outlined"
                    sx={{
                        p: {xs: 3, sm: 5},
                        borderRadius: 2,
                        borderColor: "divider"
                    }}
                >
                    <Typography
                        component="h1"
                        variant="h4"
                        sx={{
                            fontSize: {xs: 26, sm: 30},
                            fontWeight: 600,
                            mb: 2
                        }}
                    >
                        {titles[status]}
                    </Typography>
                    <Typography color="text.secondary" sx={{lineHeight: 1.7}}>
                        {descriptions[status]}
                    </Typography>
                    {status === "denied" && (
                        <Typography
                            color="text.secondary"
                            sx={{mt: 2, lineHeight: 1.7}}
                        >
                            Solicite acesso ao responsável pelo sistema,
                            informando o usuário e o ambiente exibidos acima.
                            Após a liberação, selecione “Verificar acesso” para
                            continuar nesta página.
                        </Typography>
                    )}
                    {message && (
                        <Alert severity="info" sx={{mt: 3}}>
                            {message}
                        </Alert>
                    )}
                    {status === "initializing" && (
                        <CircularProgress
                            aria-label="Verificando sessão"
                            sx={{mt: 3}}
                        />
                    )}
                    <Stack
                        direction={{xs: "column", sm: "row"}}
                        spacing={1.5}
                        sx={{mt: 3, flexWrap: "wrap", gap: 1}}
                    >
                        {status === "denied" && (
                            <Button
                                variant="contained"
                                disabled={busy}
                                onClick={() =>
                                    execute(() => api.revalidateAccess())
                                }
                            >
                                {busy ? "Verificando…" : "Verificar acesso"}
                            </Button>
                        )}
                        {["expired", "signed-out"].includes(status) && (
                            <Button
                                variant="contained"
                                disabled={busy}
                                onClick={() => execute(() => login())}
                            >
                                Entrar novamente
                            </Button>
                        )}
                        {status === "error" && (
                            <Button
                                variant="contained"
                                onClick={() => window.location.reload()}
                            >
                                Tentar novamente
                            </Button>
                        )}
                        {moduleDenied && (
                            <Button
                                component={Link}
                                to="/"
                                variant="outlined"
                                sx={{
                                    borderColor: "divider",
                                    color: "text.primary"
                                }}
                            >
                                Ir para ocorrências
                            </Button>
                        )}
                        {session.authRequired &&
                            ![
                                "initializing",
                                "configuration",
                                "error",
                                "signed-out"
                            ].includes(status) && (
                                <Button
                                    variant="outlined"
                                    sx={{
                                        borderColor: "divider",
                                        color: "text.primary"
                                    }}
                                    disabled={busy}
                                    onClick={() => execute(() => logout())}
                                >
                                    Sair da conta
                                </Button>
                            )}
                    </Stack>
                </Paper>
            </Container>
        </Box>
    );
}
